/** @jsx jsx */
import type { InputHTMLAttributes } from 'react';
import { useCallback } from 'react';
import { useEffect } from 'react';
import { useReducer } from 'react';
import { forwardRef, useMemo } from 'react';
import { jsx } from '@balance-web/core';
import { useFieldContext } from '@balance-web/field';
import { Text } from '@balance-web/text';
import { useRawTheme, useTheme } from '@balance-web/theme';
import type { InputBaseProps } from '@balance-web/text-input';
import {
  Adornment,
  AdornmentWrapper,
  useInputStyles,
} from '@balance-web/text-input';
import { buildDataAttributes } from '@balance-web/core';
import type { WithDataAttributeProp } from '@balance-web/core';
import { AlertCircleIcon } from '@balance-web/icon';

import { getCurrencyFormatter } from './utils';
import { evaluateExpression, validateInputChange } from './math';

export type CurrencyInputCalculatorProps = WithDataAttributeProp<
  {
    /** When true, a dollar symbol ($) will be displayed beside the input. */
    showAdornment?: boolean;
    onChange: (value: string | number) => void;
    value: string | number;
    maximumFractionDigits?: 0 | 2 | 4 | 5 | 8;
    minimumFractionDigits?: 2 | 4 | 5 | 8;
    align?: 'left' | 'right';
  } & Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'value' | 'onChange' | 'size'
  > &
    Partial<Omit<InputBaseProps, 'shape'>>
>;

function normaliseValue(value: string) {
  let normalisedValue = value.trim().replace(/\$/, '').replace(/,/g, '');
  return normalisedValue;
}

const parseEmittedValue = (args: {
  value: string;
  currencyFormatter: Intl.NumberFormat;
}) => {
  /**
   * We're doing normalise -> parse -> format -> normalise -> parse here
   *  because just doing normalise -> parse will mean that the parsed number will
   *  be more precise than the formatted input.
   * */

  let normalisedValue = normaliseValue(args.value);
  let formatted = args.currencyFormatter.format(parseFloat(normalisedValue));
  const renormalised = normaliseValue(formatted);
  const finalValue = parseFloat(renormalised);

  return finalValue;
};

export const CurrencyInputCalculator = forwardRef<
  HTMLInputElement,
  CurrencyInputCalculatorProps
>(
  (
    {
      maximumFractionDigits = 2,
      minimumFractionDigits = 2,
      showAdornment = true,
      size = 'medium',
      align = 'left',
      data,
      value,
      onChange,
      onBlur,
      ...consumerProps
    },
    ref
  ) => {
    const { invalid, ...a11yProps } = useFieldContext();

    const currencyFormatter = useMemo(() => {
      return getCurrencyFormatter({
        maximumFractionDigits,
        minimumFractionDigits,
      });
    }, [maximumFractionDigits, minimumFractionDigits]);

    const _parseEmittedValue = useCallback(
      (value: string) => {
        return parseEmittedValue({ value, currencyFormatter });
      },
      [currencyFormatter]
    );

    const formatInputValue = useCallback(
      (value: number) => {
        return currencyFormatter
          .formatToParts(value)
          .filter((x) => {
            return x.type !== 'currency';
          })
          .map((x) => {
            return x.value;
          })
          .join('');
      },
      [currencyFormatter]
    );

    const [calculatorState, calculatorDispatch] = useReducer(
      createCalculatorStateReducer({
        parseEmittedValue: _parseEmittedValue,
        formatInputValue: formatInputValue,
      }),
      {
        rawValue: value.toString().length
          ? formatInputValue(_parseEmittedValue(value.toString()))
          : '',
        valueToEmit: value.toString().length
          ? _parseEmittedValue(value.toString()).toString()
          : '',
      }
    );

    useEffect(
      function syncCalculatorStateToValue() {
        if (calculatorState.syncFlag === 'internal_value_changed') {
          onChange(calculatorState.valueToEmit);
          calculatorDispatch({ type: 'sync_flag_consumed' });
        } else if (value !== calculatorState.valueToEmit) {
          calculatorDispatch({ type: 'external_value_changed', value });
        }
      },
      [calculatorState.syncFlag, calculatorState.valueToEmit, onChange, value]
    );

    const inputStyles = useInputStyles({
      shape: 'square',
      size,
    });

    const { typography } = useTheme();
    const rawTheme = useRawTheme();

    const adornedStyles = useMemo(() => {
      if (!showAdornment) {
        if (invalid) {
          return {
            paddingRight:
              rawTheme.spacing[size === 'small' ? 'medium' : 'large'] * 2.25,
          };
        }
        return undefined;
      }

      return {
        paddingLeft:
          rawTheme.spacing[size === 'small' ? 'medium' : 'large'] * 2,
        paddingRight:
          rawTheme.spacing[size === 'small' ? 'medium' : 'large'] *
          (invalid ? 2.25 : 1),
        '&[readonly]': {
          ...inputStyles['&[readonly]'],
          paddingLeft: `calc((${
            rawTheme.spacing[size === 'small' ? 'medium' : 'large']
          }px * 2) - 15px)`,
          paddingRight:
            rawTheme.spacing[size === 'small' ? 'medium' : 'large'] *
            (invalid ? 2.25 : 1),
        },
      };
    }, [inputStyles, invalid, rawTheme.spacing, showAdornment, size]);

    const dataAttributes = buildDataAttributes(data);

    return (
      <AdornmentWrapper shape="square" size={size}>
        {showAdornment && (
          <Adornment
            align="left"
            css={{
              fontSize: typography.fontSize[size],
              pointerEvents: 'none',
              paddingLeft: 0,
              width: consumerProps.readOnly ? 10 : undefined,
              marginTop: consumerProps.readOnly ? 1 : undefined,
            }}
          >
            <Text
              css={{
                color: consumerProps.disabled
                  ? inputStyles[':disabled'].color
                  : inputStyles.color,
              }}
              size={size}
            >
              $
            </Text>
          </Adornment>
        )}
        <input
          ref={ref}
          aria-invalid={invalid}
          css={{
            ...inputStyles,
            ...adornedStyles,
            fontVariantNumeric: 'tabular-nums', // ensure the numbers line up when stacked
            textAlign: align,
          }}
          {...dataAttributes}
          {...a11yProps}
          disabled={consumerProps.disabled}
          placeholder={consumerProps.placeholder}
          readOnly={consumerProps.readOnly}
          value={calculatorState.rawValue}
          onBlur={(e) => {
            calculatorDispatch({ type: 'input_blur' });
            if (
              'blur' in consumerProps &&
              typeof consumerProps.blur === 'function'
            ) {
              consumerProps.blur(e);
            }
            if (
              'onBlur' in consumerProps &&
              typeof consumerProps.onBlur === 'function'
            ) {
              consumerProps.onBlur(e);
            }
          }}
          onChange={(event) => {
            calculatorDispatch({
              type: 'internal_value_changed',
              value: event.target.value,
            });
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              calculatorDispatch({ type: 'keypress_enter' });
            }

            if (consumerProps.onKeyDown) {
              consumerProps.onKeyDown(e);
            }
          }}
        />
        {invalid && (
          <Adornment
            align="right"
            css={{
              fontSize: typography.fontSize[size],
              pointerEvents: 'none',
            }}
          >
            <AlertCircleIcon color="critical" size={size} />
          </Adornment>
        )}
      </AdornmentWrapper>
    );
  }
);

type CalculatorReducerState = {
  /**
   * The value used by the internal input.
   * This can carry both the expression and the evaluated value
   *  based on user interaction and input.
   */
  rawValue: string;
  /**
   * Value that is emitted to consumer form.
   *
   * We emit a string because we cannot have an opinion if an empty input
   *  should be treated as 0 or empty or null/undefined.
   *
   * You might be tempted to change this to emit a number to simplify
   *  downstream form validation stuff but it will result in a loss of
   *  flexibility and as a result, form validation logic will leak into
   *  this component.
   */
  valueToEmit: string;
  syncFlag?: 'internal_value_changed';
};

type CalculatorReducerActions =
  | {
      type: 'internal_value_changed';
      value: string;
    }
  | {
      type: 'external_value_changed';
      value: string | number;
    }
  | {
      type: 'input_blur' | 'keypress_enter';
    }
  | {
      /**
       * Used to emit internal changes (when ready) to the onChange handler.
       *
       * We have to do this effect dance because this fun React thing
       *  https://github.com/facebook/react/issues/18178#issuecomment-595846312
       *
       */
      type: 'sync_flag_consumed';
    };

const createCalculatorStateReducer = (args: {
  parseEmittedValue: (value: string) => number;
  formatInputValue: (value: number) => string;
}) => {
  return (state: CalculatorReducerState, action: CalculatorReducerActions) => {
    switch (action.type) {
      case 'internal_value_changed': {
        /**
         * Don't validate empty value.
         */
        if (!action.value.length) {
          return {
            ...state,
            rawValue: '',
          };
        }

        const isInputChangeValid = validateInputChange(action.value);
        if (!isInputChangeValid) {
          return state;
        }

        return { ...state, rawValue: action.value };
      }
      case 'input_blur':
      case 'keypress_enter': {
        const newState = newValueToCalculatorState({
          prevState: state,
          newValue: normaliseValue(state.rawValue),
          formatInputValue: args.formatInputValue,
          parseEmittedValue: args.parseEmittedValue,
        });

        newState.syncFlag = 'internal_value_changed';
        return newState;
      }
      case 'external_value_changed': {
        const newState = newValueToCalculatorState({
          prevState: state,
          newValue: action.value.toString(),
          formatInputValue: args.formatInputValue,
          parseEmittedValue: args.parseEmittedValue,
        });

        return newState;
      }

      case 'sync_flag_consumed': {
        return {
          ...state,
          syncFlag: undefined,
        };
      }
      default: {
        return state;
      }
    }
  };
};

const newValueToCalculatorState = (args: {
  prevState: CalculatorReducerState;
  newValue: string;
  parseEmittedValue: (value: string) => number;
  formatInputValue: (value: number) => string;
}) => {
  /**
   * Empty value is not an error, we simply emit
   *  the empty value and let the consumer validation
   *  decided if it should default to 0 or be an error.
   */
  if (!args.newValue.length) {
    return {
      ...args.prevState,
      rawValue: '',
      valueToEmit: '',
    };
  }

  const evaluationResult = evaluateExpression(args.newValue);
  let valueToEmit = '';
  let rawValue = '';
  if (evaluationResult.success) {
    const parsedValue = args.parseEmittedValue(
      evaluationResult.evaluatedValue.toString()
    );
    valueToEmit = parsedValue.toString();
    rawValue = args.formatInputValue(parsedValue);
  } else {
    /**
     * When the evaluation fails we emit the invalid raw value
     *  and let consumer form validation decied what to do.
     */
    valueToEmit = evaluationResult.expression;
    rawValue = evaluationResult.expression;
  }

  return {
    ...args.prevState,
    valueToEmit,
    rawValue,
  };
};
